import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePayment } from '../../contexts/PaymentContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCreditCard, FiSave, FiEye, FiEyeOff, FiSettings } = FiIcons;

const PaymentSettings = () => {
  const { paymentSettings, updatePaymentSettings } = usePayment();
  const [formData, setFormData] = useState(paymentSettings);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    updatePaymentSettings(formData);
    setUnsavedChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Payment Settings</h2>
          <p className="text-warm-brown/70">Configure your payment processing</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!unsavedChanges}
          className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SafeIcon icon={FiSave} className="text-sm" />
          Save Changes
        </button>
      </div>

      {unsavedChanges && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-orange-50 border border-orange-200 text-orange-700 px-4 py-3 rounded-lg"
        >
          You have unsaved changes. Don't forget to save your settings!
        </motion.div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Stripe Configuration */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-playfair text-warm-brown mb-4 flex items-center gap-2">
            <SafeIcon icon={FiCreditCard} className="text-lg" />
            Stripe Configuration
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-warm-brown font-medium mb-2">
                Publishable Key
              </label>
              <input
                type="text"
                value={formData.stripePublishableKey}
                onChange={(e) => handleInputChange('stripePublishableKey', e.target.value)}
                placeholder="pk_test_..."
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
              <p className="text-xs text-warm-brown/60 mt-1">
                Your Stripe publishable key (safe to expose publicly)
              </p>
            </div>

            <div>
              <label className="block text-warm-brown font-medium mb-2">
                Secret Key
              </label>
              <div className="relative">
                <input
                  type={showSecretKey ? 'text' : 'password'}
                  value={formData.stripeSecretKey}
                  onChange={(e) => handleInputChange('stripeSecretKey', e.target.value)}
                  placeholder="sk_test_..."
                  className="w-full px-4 py-2 pr-12 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <SafeIcon 
                    icon={showSecretKey ? FiEyeOff : FiEye} 
                    className="text-warm-brown/50 hover:text-warm-brown transition-colors" 
                  />
                </button>
              </div>
              <p className="text-xs text-warm-brown/60 mt-1">
                Your Stripe secret key (keep this secure)
              </p>
            </div>

            <div>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  className="w-4 h-4 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                />
                <span className="text-warm-brown font-medium">Enable Stripe Payments</span>
              </label>
              <p className="text-xs text-warm-brown/60 mt-1 ml-7">
                Allow customers to pay with credit/debit cards
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Configuration */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-playfair text-warm-brown mb-4 flex items-center gap-2">
            <SafeIcon icon={FiSettings} className="text-lg" />
            Pricing Configuration
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-warm-brown font-medium mb-2">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              >
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="CAD">CAD - Canadian Dollar</option>
              </select>
            </div>

            <div>
              <label className="block text-warm-brown font-medium mb-2">
                Tax Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={(formData.taxRate * 100).toFixed(2)}
                onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) / 100 || 0)}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
              <p className="text-xs text-warm-brown/60 mt-1">
                Sales tax applied to orders
              </p>
            </div>

            <div>
              <label className="block text-warm-brown font-medium mb-2">
                Processing Fee (%)
              </label>
              <input
                type="number"
                step="0.001"
                min="0"
                max="10"
                value={(formData.processingFee * 100).toFixed(3)}
                onChange={(e) => handleInputChange('processingFee', parseFloat(e.target.value) / 100 || 0)}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
              <p className="text-xs text-warm-brown/60 mt-1">
                Credit card processing fee (typically 2.9%)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Payment Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-playfair text-warm-brown mb-4">Test Payment Integration</h3>
        
        <div className="bg-cream rounded-lg p-4 mb-4">
          <h4 className="font-medium text-warm-brown mb-2">Test Card Numbers:</h4>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-warm-brown/80">Visa:</p>
              <p className="font-mono text-warm-brown/70">4242 4242 4242 4242</p>
            </div>
            <div>
              <p className="font-medium text-warm-brown/80">Mastercard:</p>
              <p className="font-mono text-warm-brown/70">5555 5555 5555 4444</p>
            </div>
            <div>
              <p className="font-medium text-warm-brown/80">American Express:</p>
              <p className="font-mono text-warm-brown/70">3782 822463 10005</p>
            </div>
            <div>
              <p className="font-medium text-warm-brown/80">Declined:</p>
              <p className="font-mono text-warm-brown/70">4000 0000 0000 0002</p>
            </div>
          </div>
          <p className="text-xs text-warm-brown/60 mt-2">
            Use any future expiry date and any 3-digit CVV for testing
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">🔒 Security Note:</h4>
          <p className="text-sm text-blue-700">
            Your secret key is stored locally in your browser. For production use, 
            ensure your keys are properly secured and never expose your secret key in client-side code.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;